// CameraMonitorPopup.js
import React, { useEffect, useState, useCallback } from 'react';
import { fetchCameraMonitor, fetchCameraMonitorFromInstance, buildCameraUrls } from '../services/cameraMonitorApi';
import './CSS/CameraMonitor.css';

const CameraMonitorPopup = () => {
  const [camera, setCamera] = useState(null);
  const [baseUrl, setBaseUrl] = useState('');
  const [instanceId, setInstanceId] = useState(null);
  const [monitorData, setMonitorData] = useState(null);
  const [dataSource, setDataSource] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5000);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cameraParam = params.get('camera');
    const baseUrlParam = params.get('baseUrl');
    const instanceIdParam = params.get('instanceId');

    if (instanceIdParam) setInstanceId(instanceIdParam);
    if (cameraParam && baseUrlParam) {
      try {
        setCamera(JSON.parse(decodeURIComponent(cameraParam)));
        setBaseUrl(decodeURIComponent(baseUrlParam));
      } catch (error) {
        console.error('Error parsing camera parameters:', error);
      }
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!camera || !baseUrl) return;

    // Try instance proxy first (works before event starts)
    if (instanceId) {
      const instanceResult = await fetchCameraMonitorFromInstance(instanceId);
      if (instanceResult.success) {
        setMonitorData(instanceResult);
        setDataSource('instance');
        setLoading(false);
        return;
      }
    }

    // Fall back to CloudFront (works after event starts + sync-x)
    const result = await fetchCameraMonitor(baseUrl, camera.path);
    setMonitorData(result);
    setDataSource(result.success ? 'cloudfront' : null);
    setLoading(false);
  }, [camera, baseUrl, instanceId]);

  useEffect(() => {
    fetchData();

    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchData();
      }, refreshInterval);

      return () => clearInterval(interval);
    }
  }, [fetchData, autoRefresh, refreshInterval]);

  const getHealthColor = (status) => {
    switch (status) {
      case 'good': return '#4CAF50';
      case 'warning': return '#FF9800';
      case 'error': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const formatKbps = (kbps) => {
    if (kbps == null) return 'N/A';
    if (kbps >= 1000) return `${(kbps / 1000).toFixed(1)} Mbps`;
    return `${kbps} kbps`;
  };

  const formatDuration = (sec) => {
    if (sec == null) return 'N/A';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = Math.floor(sec % 60);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const formatMB = (mb) => {
    if (mb == null || mb === 0) return 'N/A';
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb.toFixed(2)} MB`;
  };

  // Mismos umbrales que calculate_health() en rtmp-monitor.py: 85% warn, 95% critical.
  const getDiskColor = (percent) => {
    if (percent == null) return '#999';
    if (percent >= 95) return '#f44336';
    if (percent >= 85) return '#ff9800';
    return '#4caf50';
  };

  const DISK_LABELS = {
    root:     'Root (/)',
    segments: 'Segments (remote)',
    srs_hls:  'SRS HLS',
  };

  if (!camera || !baseUrl) {
    return (
      <div className="camera-monitor popup-mode">
        <div className="loading-screen">
          <h2>Loading camera monitor...</h2>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="camera-monitor popup-mode">
        <div className="loading-screen">
          <h2>Loading monitor data...</h2>
        </div>
      </div>
    );
  }

  if (!monitorData || !monitorData.success) {
    const urls = buildCameraUrls(baseUrl, camera.path);
    return (
      <div className="camera-monitor popup-mode">
        <div className="monitor-header">
          <h2>{camera.name}</h2>
          <div className="controls">
            <button onClick={() => window.close()} style={{ padding: '8px 16px', background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Close
            </button>
          </div>
        </div>
        <div className="monitor-card error">
          <p className="error-message">Error: {monitorData?.error || 'Failed to load monitor data'}</p>
          <div style={{ marginTop: '20px' }}>
            <a href={urls.monitor} target="_blank" rel="noopener noreferrer" style={{ color: '#2196F3' }}>
              Try opening monitor JSON directly
            </a>
          </div>
        </div>
      </div>
    );
  }

  const m = monitorData.data;
  const healthColor = getHealthColor(m.health?.status);
  const urls = buildCameraUrls(baseUrl, camera.path);

  return (
    <div className="camera-monitor popup-mode">
      <div className="monitor-header">
        <h2>{camera.name}</h2>
        {dataSource && (
          <span style={{ fontSize: '11px', color: dataSource === 'instance' ? '#ff9800' : '#4caf50', marginLeft: '8px' }}>
            [{dataSource === 'instance' ? 'Instance' : 'CloudFront'}]
          </span>
        )}
        <div className="controls">
          <label>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto Refresh
          </label>
          {autoRefresh && (
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
            >
              <option value={2000}>2s</option>
              <option value={5000}>5s</option>
              <option value={10000}>10s</option>
              <option value={30000}>30s</option>
            </select>
          )}
          <button onClick={fetchData} className="refresh-btn">
            Refresh
          </button>
          <button onClick={() => window.close()} style={{ padding: '8px 16px', background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Close
          </button>
        </div>
      </div>

      <div className="monitor-card">
        <div className="monitor-info">
          {/* Health */}
          <div className="info-section">
            <h4>Health</h4>
            <div className="quality-status" style={{ borderColor: healthColor }}>
              <div className="quality-score" style={{ color: healthColor }}>
                {m.health?.score ?? 0}
              </div>
              <div className="quality-label" style={{ color: healthColor }}>
                {m.health?.status || 'unknown'}
              </div>
            </div>
            {m.health?.issues && m.health.issues.length > 0 && (
              <div className="quality-issues">
                <strong>Issues:</strong>
                <ul>
                  {m.health.issues.map((issue, idx) => (
                    <li key={idx}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Ingest */}
          <div className="info-section">
            <h4>Ingest</h4>
            <div className="status-grid">
              <div className="status-item">
                <span className="label">Active:</span>
                <span className={`value ${m.ingest?.active ? 'active' : 'inactive'}`}>
                  {m.ingest?.active ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="status-item">
                <span className="label">Protocol:</span>
                <span className="value">{m.ingest?.protocol?.toUpperCase() || 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Stream:</span>
                <span className="value">{m.ingest?.stream_name || 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Source IP:</span>
                <span className="value">{m.ingest?.ip || 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Bitrate:</span>
                <span className="value">{formatKbps(m.ingest?.kbps_in)}</span>
              </div>
              <div className="status-item">
                <span className="label">Resolution:</span>
                <span className="value">{m.ingest?.resolution || 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Codec:</span>
                <span className="value">{m.ingest?.codec || 'N/A'}{m.ingest?.audio_codec ? ` / ${m.ingest.audio_codec}` : ''}</span>
              </div>
              <div className="status-item">
                <span className="label">Alive:</span>
                <span className="value">{formatDuration(m.ingest?.alive_sec)}</span>
              </div>
            </div>
          </div>

          {/* Slate */}
          <div className="info-section">
            <h4>Slate</h4>
            <div className="status-grid">
              <div className="status-item">
                <span className="label">Active:</span>
                <span className={`value ${m.slate?.active ? 'active' : 'inactive'}`}>
                  {m.slate?.active ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="status-item">
                <span className="label">Bitrate:</span>
                <span className="value">{formatKbps(m.slate?.kbps_in)}</span>
              </div>
              <div className="status-item">
                <span className="label">Resolution:</span>
                <span className="value">{m.slate?.resolution || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Proxy */}
          <div className="info-section">
            <h4>HLS Proxy</h4>
            <div className="status-grid">
              <div className="status-item">
                <span className="label">Running:</span>
                <span className={`value ${m.proxy?.running ? 'active' : 'inactive'}`}>
                  {m.proxy?.running ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="status-item">
                <span className="label">Active Source:</span>
                <span className="value" style={{ color: m.proxy?.active_source === 'livestream' ? '#4caf50' : '#ff9800' }}>
                  {m.proxy?.active_source || 'N/A'}
                </span>
              </div>
              <div className="status-item">
                <span className="label">Switches:</span>
                <span className="value">{m.proxy?.switch_count ?? 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">m3u8 Age:</span>
                <span className="value">{m.proxy?.active_m3u8_age_ms != null ? `${m.proxy.active_m3u8_age_ms} ms` : 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Encoder */}
          <div className="info-section">
            <h4>Encoder</h4>
            <div className="status-grid">
              <div className="status-item">
                <span className="label">Running:</span>
                <span className={`value ${m.encoder?.running ? 'active' : 'inactive'}`}>
                  {m.encoder?.running ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="status-item">
                <span className="label">Speed:</span>
                <span className={`value ${m.encoder?.speed && m.encoder.speed >= 0.95 ? 'active' : (m.encoder?.speed ? 'inactive' : '')}`}>
                  {m.encoder?.speed != null ? `${m.encoder.speed}x` : 'N/A'}
                </span>
              </div>
              <div className="status-item">
                <span className="label">FPS:</span>
                <span className="value">{m.encoder?.fps ?? 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Output Bitrate:</span>
                <span className="value">{formatKbps(m.encoder?.output_bitrate_kbps)}</span>
              </div>
              <div className="status-item">
                <span className="label">CPU:</span>
                <span className="value">{m.encoder?.cpu_percent != null ? `${m.encoder.cpu_percent.toFixed(1)}%` : 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Memory:</span>
                <span className="value">{m.encoder?.memory_mb != null ? `${m.encoder.memory_mb.toFixed(0)} MB` : 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Encoded Time:</span>
                <span className="value">{m.encoder?.encoded_time || 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Encoded Size:</span>
                <span className="value">{formatMB(m.encoder?.encoded_size_mb)}</span>
              </div>
            </div>
          </div>

          {/* Segments */}
          <div className="info-section">
            <h4>DASH Segments</h4>
            <div className="status-grid">
              <div className="status-item">
                <span className="label">Active:</span>
                <span className={`value ${m.segments?.active ? 'active' : 'inactive'}`}>
                  {m.segments?.active ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="status-item">
                <span className="label">Count:</span>
                <span className="value">{m.segments?.segment_count ?? 0}</span>
              </div>
              <div className="status-item">
                <span className="label">Latest Age:</span>
                <span className="value">{m.segments?.latest_age_sec != null ? `${m.segments.latest_age_sec.toFixed(1)}s` : 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Rate:</span>
                <span className="value">{m.segments?.segments_per_min != null ? `${m.segments.segments_per_min.toFixed(1)}/min` : 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Total Size:</span>
                <span className="value">{formatMB(m.segments?.total_size_mb)}</span>
              </div>
            </div>
          </div>

          {/* Disk */}
          {m.disk && (
            <div className="info-section">
              <h4>Disk</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #ddd' }}>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Mount</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>Free</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>Total</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>Used</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(m.disk).map(([key, d]) => (
                    <tr key={key} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '4px 6px' }}>
                        {DISK_LABELS[key] || key}
                        <span style={{ color: '#999', marginLeft: 6, fontFamily: 'monospace' }}>{d.path}</span>
                      </td>
                      {d.available ? (
                        <>
                          <td style={{ padding: '4px 6px', textAlign: 'right' }}>{d.free_gb} GB</td>
                          <td style={{ padding: '4px 6px', textAlign: 'right' }}>{d.total_gb} GB</td>
                          <td style={{ padding: '4px 6px', textAlign: 'right', color: getDiskColor(d.percent_used), fontWeight: 'bold' }}>
                            {d.percent_used}%
                          </td>
                        </>
                      ) : (
                        /* En 'segments' esto suele significar que el mount SSHFS ha caido */
                        <td colSpan={3} style={{ padding: '4px 6px', textAlign: 'right', color: '#f44336' }}>
                          unreadable
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* SRS Streams */}
          {m.srs?.streams && m.srs.streams.length > 0 && (
            <div className="info-section">
              <h4>SRS Streams {m.srs?.connected ? '' : '(disconnected)'}</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #ddd' }}>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Proto</th>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>IP</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>Bitrate</th>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Video</th>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {m.srs.streams.map((s, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '4px 6px', fontWeight: s.is_camera ? 'bold' : 'normal' }}>{s.app}/{s.name}</td>
                      <td style={{ padding: '4px 6px' }}>{s.protocol?.toUpperCase()}</td>
                      <td style={{ padding: '4px 6px', fontFamily: 'monospace' }}>{s.ip}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right' }}>{formatKbps(s.kbps_in)}</td>
                      <td style={{ padding: '4px 6px' }}>{s.video ? `${s.video.codec} ${s.video.width}x${s.video.height}` : 'N/A'}</td>
                      {/* is_camera excluye slate Y normalized, asi que 'no camera'
                          ya no implica slate: hay que mirar is_slate. */}
                      <td style={{ padding: '4px 6px', color: s.is_camera ? '#4caf50' : (s.is_slate ? '#ff9800' : '#999') }}>
                        {s.is_camera ? 'Camera' : (s.is_slate ? 'Slate' : 'Internal')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Drift */}
          {m.drift && (
            <div className="info-section">
              <h4>Drift</h4>
              <div className="status-grid">
                <div className="status-item">
                  <span className="label">Lag:</span>
                  <span className={`value ${m.drift.lag_seconds <= 2 ? 'active' : 'inactive'}`}>
                    {m.drift.lag_seconds != null ? `${m.drift.lag_seconds.toFixed(2)}s` : 'N/A'}
                  </span>
                </div>
                <div className="status-item">
                  <span className="label">Acc. Drift:</span>
                  <span className={`value ${m.drift.accumulated_drift_seconds != null && m.drift.accumulated_drift_seconds <= 3 ? 'active' : 'inactive'}`}>
                    {m.drift.accumulated_drift_seconds != null ? `${m.drift.accumulated_drift_seconds.toFixed(2)}s` : 'N/A'}
                  </span>
                </div>
                <div className="status-item">
                  <span className="label">Active Source:</span>
                  <span className="value" style={{ color: m.drift.active_source === 'slate' ? '#ff9800' : '#4caf50' }}>
                    {m.drift.active_source || 'N/A'}
                  </span>
                </div>
                <div className="status-item">
                  <span className="label">Switches:</span>
                  <span className="value">{m.drift.switch_count ?? 'N/A'}</span>
                </div>
                <div className="status-item">
                  <span className="label">Last Switch:</span>
                  <span className="value">
                    {m.drift.last_switch ? new Date(m.drift.last_switch).toLocaleTimeString() : 'N/A'}
                  </span>
                </div>
                <div className="status-item">
                  <span className="label">Last Segment:</span>
                  <span className="value">#{m.drift.last_segment_number ?? 'N/A'}</span>
                </div>
                <div className="status-item">
                  <span className="label">Demo.sh:</span>
                  <span className={`value ${m.drift.demosh_alive ? 'active' : 'inactive'}`}>
                    {m.drift.demosh_alive ? 'Alive' : 'Dead'}
                  </span>
                </div>
                <div className="status-item">
                  <span className="label">Init Segment:</span>
                  <span className={`value ${m.drift.init_segment_exists ? 'active' : 'inactive'}`}>
                    {m.drift.init_segment_exists ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Timestamp & URLs */}
          <div className="info-section">
            <div className="status-grid">
              <div className="status-item">
                <span className="label">Last Update:</span>
                <span className="value">
                  {m.timestamp ? new Date(m.timestamp).toLocaleTimeString() : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          <div className="info-section urls">
            <h4>Stream URLs</h4>
            <div className="url-item">
              <span className="label">MPD:</span>
              <a href={urls.mpd} target="_blank" rel="noopener noreferrer" className="url-link">
                {urls.mpd}
              </a>
            </div>
            <div className="url-item">
              <span className="label">Monitor JSON:</span>
              <a href={urls.monitor} target="_blank" rel="noopener noreferrer" className="url-link">
                {urls.monitor}
              </a>
            </div>
            <div className="url-item">
              <span className="label">History JSON:</span>
              <a href={urls.history} target="_blank" rel="noopener noreferrer" className="url-link">
                {urls.history}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CameraMonitorPopup;
